// noinspection NpmUsedModulesInstalled,ES6CheckImport,DuplicatedCode

import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { eachDayOfInterval } from 'date-fns';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import ReportConversionContext from '@/modules/reports/contexts/ReportConversionContext';
import Paper from '@/modules/common/components/Paper';
import Content from '@/modules/common/components/Content';
import Spinner from '@/modules/common/components/Spinner';
import ErrorFetch from '@/modules/common/components/ErrorFetch';
import ContentBody from '@/modules/common/components/ContentBody';
import ContentHeader from '@/modules/common/components/ContentHeader';
import { withFetch } from '~/hocs/fetch';
import { withRouter } from '~/hocs/router';
import { parseDate, formatDate, isEmptyObject } from '~/common';
import { actionDockSetExpandReports } from '@/modules/application/actions/dock';
import Top from './Top';
import TableLeft from './TableLeft/TableLeft';
import TableRight from './TableRight/TableRight';
import '../../styles/ReportConversion.scss';

const initialValuesFilter = {
    typeName: [],
    date: {from: new Date(), to: new Date()},
};

function isFiltered(urlSearchParams) {
    return (
        urlSearchParams.get('type_name') ||
        urlSearchParams.get('date_from') ||
        urlSearchParams.get('date_to')
    );
}

let abortController;

const ReportConversion = (props) => {
    let breadcrumbs = [
        {name: 'Отчеты'},
        {name: 'Конверсия'},
    ];

    const { offerName, affiliateName } = useParams();

    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [dateRange, setDateRange] = useState({from: new Date(), to: new Date()});
    const [valuesFilter, setValuesFilter] = useState(initialValuesFilter);
    const [ordering, setOrdering] = useState('primary');

    if (offerName) {
        breadcrumbs[1].to = '/reports/conversion/';
        breadcrumbs.push({name: offerName});
    }
    if (affiliateName) {
        breadcrumbs[2].to = `/reports/conversion/${offerName}/`;
        breadcrumbs.push({name: affiliateName});
    }

    const fetch = () => {
        abortController = new AbortController();

        let url = '/api/reports/conversion/';
        if (affiliateName) {
            url += `${offerName}/${affiliateName}/`;
        }
        else if (offerName) {
            url += `${offerName}/`;
        }
        url += `?${props.router.urlSearchParams.toString()}`;

        setLoading(true);
        props.fetch.get(url, abortController)
            .then(({ok, data}) => {
                if (ok) {
                    let _report = {};
                    if (data.report) {
                        let entries = Object.entries(data.report);
                        entries = entries.map((element) => [' ' + element[0], element[1]]);
                        entries.sort(([a,],[b,]) => a > b ? 1 : -1);
                        _report = Object.fromEntries(entries);
                    }
                    setReport(_report);
                }
                setLoading(false);
            })
            .catch((error) => {
                setReport(null);
                if (error.name !== 'AbortError') {
                    setLoading(false);
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    useEffect(() => {
        props.dockSetExpandReports(true);
        return () => {
            if (abortController) {
                abortController = null;
            }
        };
    }, []);

    useEffect(() => {
        const date = new Date();
        let date_from = props.router.urlSearchParams.get('date_from');
        let date_to = props.router.urlSearchParams.get('date_to');
        date_from = parseDate(date_from, 'dd.MM.yy');
        date_to = parseDate(date_to, 'dd.MM.yy');
        if (!date_from) {
            date_from = new Date(date.getFullYear(), date.getMonth(), 1);
        }
        if (!date_to) {
            date_to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        }
        setDateRange({from: date_from, to: date_to});
        setValuesFilter({
            typeName: props.router.getUrlSearchParam('type_name').split('||'),
            date: {from: date_from, to: date_to}
        });
        fetch();
    }, [props.history.location.search]);

    const handleClickResetFilter = () => {
        props.history.push({search: ''});
    };

    const handleSubmitFilter = (event) => {
        event.preventDefault();
        props.router.urlSearchParams.set('type_name', valuesFilter.typeName.join('||'));
        props.router.urlSearchParams.set('date_from', formatDate(valuesFilter.date.from));
        props.router.urlSearchParams.set('date_to', formatDate(valuesFilter.date.to));
        props.history.push({search: props.router.urlSearchParams.toString()});
    };

    const handleClickOrder = (newOrdering) => {
        // noinspection JSCheckFunctionSignatures
        let entries = Object.entries(report);
        if (newOrdering === 'primary') {
            entries = entries.sort(([a,],[b,]) => a > b ? 1 : -1);
        }
        else if (newOrdering === '-primary') {
            entries = entries.sort(([a,], [b,]) => a < b ? 1 : -1);
        }
        else if (newOrdering === 'total') {
            entries = entries.sort(([,a], [,b]) => a.total > b.total ? 1 : -1);
        }
        else if (newOrdering === '-total') {
            entries = entries.sort(([,a], [,b]) => a.total < b.total ? 1 : -1);
        }
        else if (newOrdering === 'avg') {
            // noinspection JSUnresolvedVariable
            entries = entries.sort(([,a], [,b]) => a.conversion > b.conversion ? 1 : -1);
        }
        else if (newOrdering === '-avg') {
            // noinspection JSUnresolvedVariable
            entries = entries.sort(([,a], [,b]) => a.conversion < b.conversion ? 1 : -1);
        }
        else if (newOrdering[0] !== '-') {
            entries = entries.sort(([,a], [,b]) => {
                let _a = a[newOrdering];
                let _b = b[newOrdering];
                // noinspection JSUnresolvedVariable
                _a = (_a) ? _a.conversion : 0;
                // noinspection JSUnresolvedVariable
                _b = (_b) ? _b.conversion : 0;
                return _a > _b ? 1 : -1;
            });
        }
        else {
            const key = newOrdering.substring(1);
            entries = entries.sort(([,a], [,b]) => {
                let _a = a[key];
                let _b = b[key];
                // noinspection JSUnresolvedVariable
                _a = (_a) ? _a.conversion : 0;
                // noinspection JSUnresolvedVariable
                _b = (_b) ? _b.conversion : 0;
                return _a < _b ? 1 : -1;
            });
        }
        setReport(Object.fromEntries(entries));
        setOrdering(newOrdering);
    };

    return (
        <ReportConversionContext.Provider value={{ordering: ordering, handleClickOrder: handleClickOrder}}>
            <Content id="report-conversion">
                <ContentHeader breadcrumbs={breadcrumbs}/>
                <ContentBody><Paper>
                    {(!loading)
                        ? <>
                            {(report)
                                ? <>
                                    <Top report={report}
                                         valuesFilter={valuesFilter} setValuesFilter={setValuesFilter}
                                         isFiltered={Boolean(isFiltered(props.router.urlSearchParams))}
                                         onSubmitFilter={handleSubmitFilter} onClickResetFilter={handleClickResetFilter}
                                    />
                                    {(!isEmptyObject(report))
                                        ? <div className="report">
                                            <TableLeft report={report}/>
                                            <TableRight report={report}
                                                        days={eachDayOfInterval({
                                                            start: dateRange.from, end: dateRange.to
                                                        })}
                                            />
                                        </div>
                                        : null
                                    }
                                </>
                                : <ErrorFetch onClick={() => fetch()}/>
                            }
                        </>
                        : <Spinner/>
                    }
                </Paper></ContentBody>
            </Content>
        </ReportConversionContext.Provider>
    );
};

let Component = ReportConversion;
Component = withRouter(Component);
Component = withFetch(Component);

export default connect(
    null,
    (dispatch) => ({
        dockSetExpandReports: bindActionCreators(actionDockSetExpandReports, dispatch),
    }),
)(Component);
